/**
 * Which Quick Plan this advisor gets, read by every Quick Plan entry point and by the dialog.
 *
 * A thin wrapper over `resolveQuickPlanAvailability` in @shared, which the server also enforces
 * (`util/resolveShortFormAccess.ts`), so the UI can never offer a profile the API would refuse.
 * The AppConfig flags come from `useFeatureFlags`, which the server resolves with the advisor's
 * organization bucket applied, the same way it checks `edjQuickPlan` on submit.
 *
 * `isEdjQuickPlan` is true only for an Edward Jones advisor with `edjQuickPlan` on. Everyone else,
 * including EDJ advisors with the flag off, keeps the existing Quick Plan behaviour, which for EDJ
 * means the `quick-start` pilot restriction still applies.
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { EDJ_QUICK_PLAN_PROFILE, resolveQuickPlanAvailability } from '@shared';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { selectFeatureFlags } from '@/model/selectors';

export function useQuickPlanAvailability() {
  const advisorFeatureFlags = useSelector(selectFeatureFlags);
  const { flags } = useFeatureFlags();

  return useMemo(() => {
    const availability = resolveQuickPlanAvailability({
      advisorFeatureFlags,
      appConfigFlags: flags,
    });

    return {
      ...availability,
      isEdjQuickPlan: availability.available && availability.profile === EDJ_QUICK_PLAN_PROFILE,
    };
  }, [advisorFeatureFlags, flags]);
}
